import React, { useCallback, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { LeapClaim, DebtorClaimStage, DebtCollectionCaseType } from '../../../api/client';
import { formatDate } from '../../Utils';
import currencyFormatter from 'currency-formatter';

interface Props {
  debtorClaim: LeapClaim;
}

const getIdentifierName = (transferredToCollection: boolean) =>
  transferredToCollection ? 'Saksnummer:' : 'Fakturanummer:';

const getClaimStageName = (claimStage: DebtorClaimStage | undefined, caseType: DebtCollectionCaseType) => {
  switch (caseType) {
    case DebtCollectionCaseType.Judicial:
      return 'Rettslig inkasso';
    case DebtCollectionCaseType.Minor:
      return 'Purring';
  }

  switch (claimStage) {
    case DebtorClaimStage.Invoice:
      return 'Faktura';
    case DebtorClaimStage.Reminder:
      return 'Purring';
    case DebtorClaimStage.DebtCollectionWarning:
      return 'Inkassovarsel';
    case DebtorClaimStage.DebtCollection:
      return 'Inkasso';
    default:
      return '';
  }
};

export default function ClaimPaymentInfoCopyButton({ debtorClaim }: Props) {
  const [copyTooltip, setCopyTooltip] = useState('Kopier betalingsinformasjon');
  const [copied, setCopied] = useState(false);

  const handleCopyClick = useCallback(() => {
    const dueDate = ((debtorClaim.claimStage ?? DebtorClaimStage.Undefined) in [DebtorClaimStage.Invoice, DebtorClaimStage.Reminder, DebtorClaimStage.DebtCollectionWarning])
      ? formatDate(debtorClaim.dueDate)
      : undefined;
    const outstandingAmount = debtorClaim.amount != null
      ? currencyFormatter.format(debtorClaim.amount, { code: 'NOK' })
      : '';

    const lines = [
      debtorClaim.number ? `${getIdentifierName(debtorClaim.isTransferredToCollection ?? false)} ${debtorClaim.number}` : null,
      debtorClaim.claimStage ? `Status: ${getClaimStageName(debtorClaim.claimStage, debtorClaim.caseType!)}` : null,
      outstandingAmount ? `Beløp å betale: ${outstandingAmount}` : null,
      debtorClaim.kid ? `KID: ${debtorClaim.kid}` : null,
      debtorClaim.debtorPaymentAccount ? `Til konto: ${debtorClaim.debtorPaymentAccount}` : null,
      dueDate ? `Forfall: ${dueDate}` : null,
    ].filter(Boolean).join('\n');

    navigator.clipboard.writeText(lines).then(() => {
      setCopied(true);
      setCopyTooltip('Kopiert!');
      setTimeout(() => {
        setCopied(false);
        setCopyTooltip('Kopier betalingsinformasjon');
      }, 2000);
    });
  }, [debtorClaim]);

  return (
    <Tooltip
      title={copyTooltip}
      placement="top"
      componentsProps={{
        tooltip: {
          sx: { fontSize: '0.80rem' },
        },
      }}
    >
      <IconButton
        size="small"
        onClick={handleCopyClick}
        sx={{ ml: '3px', mt: '-3px', padding: '2px' }}
        aria-label="Kopier betalingsinformasjon"
      >
        {copied ? <CheckIcon sx={{ fontSize: '14px', width: '1.2em', height: '1.2em' }} /> : <ContentCopyIcon sx={{ fontSize: '14px', width: '1.2em', height: '1.2em' }} />}
      </IconButton>
    </Tooltip>
  );
}